import {Component, OnInit} from '@angular/core';
import {AlertService} from "../services";
import {FormArray, FormBuilder, FormGroup, Validators} from '@angular/forms';

import { Router, ActivatedRoute } from '@angular/router';
import { first } from 'rxjs/operators';
import {MatDialog} from "@angular/material/dialog";
import {ComponentTitleService} from "../services/componentTitle.service";
import {FuseTranslationLoaderService} from "../../../@fuse/services/translation-loader.service";

import { locale as polish } from './i18n/pl';
import {fuseAnimations} from "../../../@fuse/animations";
import {MatSnackBar} from "@angular/material/snack-bar";
import {Service} from "../models/service";
import {MessageTemplate} from "../models/messageTemplate";
import {MessageTemplateService} from "../services/messageTemplate.service";
import {TestMessageDialogComponent} from "./dialog/testMessageDialog.component";
import {NotificationAccountList} from "../models/notificationAccountList";
import {NotificationAccount} from "../models/notificationAccount";

@Component({
    templateUrl: './messageTemplateForm.component.html',
    styleUrls: ['./messageTemplateForm.component.scss'],
    animations   : fuseAnimations
})

export class MessageTemplateFormComponent implements OnInit {

    form: FormGroup;
    loading = false;
    submitted = false;
    id: string;
    isAddMode: boolean;
    dialogRef: any;
    availableServices: Service[];
    deleteDialogRef: any;
    notificationAccounts: NotificationAccount[];

    constructor(
        public _matDialog: MatDialog,
        private formBuilder: FormBuilder,
        private route: ActivatedRoute,
        private router: Router,
        private alertService: AlertService,
        private componentTitleService: ComponentTitleService,
        private translationLoader: FuseTranslationLoaderService,
        private _matSnackBar: MatSnackBar,
        private messageTemplateService: MessageTemplateService,
    ) {
        this.translationLoader.loadTranslations(polish);

        componentTitleService.clearTitle();
        componentTitleService.addPart('Message templates');
    }

    ngOnInit() {
        this.notificationAccounts = this.route.snapshot.data.notificationAccounts.items;
        this.id = this.route.snapshot.params['id'];
        this.isAddMode = !this.id;

        if (this.isAddMode) {
            this.componentTitleService.addPart('New');
        } else {
            this.componentTitleService.addPart('Edit');
        }

        this.form = this.formBuilder.group({
            name: ['', Validators.required],
            description: ['', Validators.required],
            notificationAccount: [''],
            customerRegistrationMessage: [false],
            customerConfirmMessage: [false],
            orderPaymentStartedMessage: [false],
            orderPaidMessage: [false],
            orderPaymentErrorMessage: [false],
            orderAssignedToCartMessage: [false],
            orderImpossibleToAssignToCardMessage: [false],
            resetPasswordMessage: [false],
            deletedAccountMessage: [false],
            tempPasswordMessage: [false],
            accountDeletionMessage: [false],
        });

        if (!this.isAddMode) {
            this.messageTemplateService.getById(this.id)
                .pipe(first())
                .subscribe((x: MessageTemplate) => {
                    this.f.name.setValue(x.name);
                    this.f.description.setValue(x.description);

                    if (x.notificationAccount) {
                        this.f.notificationAccount.setValue(x.notificationAccount.id);
                    }

                    this.f.customerRegistrationMessage.setValue(x.customerRegistrationMessage);
                    this.f.customerConfirmMessage.setValue(x.customerConfirmMessage);
                    this.f.orderPaymentStartedMessage.setValue(x.orderPaymentStartedMessage);
                    this.f.orderPaidMessage.setValue(x.orderPaidMessage);
                    this.f.orderPaymentErrorMessage.setValue(x.orderPaymentErrorMessage);
                    this.f.orderAssignedToCartMessage.setValue(x.orderAssignedToCartMessage);
                    this.f.orderImpossibleToAssignToCardMessage.setValue(x.orderImpossibleToAssignToCardMessage);
                    this.f.resetPasswordMessage.setValue(x.resetPasswordMessage);
                    this.f.deletedAccountMessage.setValue(x.deletedAccountMessage);
                    this.f.tempPasswordMessage.setValue(x.tempPasswordMessage);
                    this.f.tempPasswordMessage.setValue(x.accountDeletionMessage);
                });
        }
    }

    // convenience getter for easy access to form fields
    get f() { return this.form.controls; }

    onSubmit() {
        this.submitted = true;

        // reset alerts on submit
        // this.alertService.clear();

        // stop here if form is invalid
        if (this.form.invalid) {
            return;
        }

        this.loading = true;

        if (this.isAddMode) {
            this.createMessageTemplate();
        } else {
            this.updateMessageTemplate();
        }
    }

    private createMessageTemplate() {
        this.messageTemplateService.create(this.form.value)
            .pipe(first())
            .subscribe(
                data => {
                    this._matSnackBar.open('Message template added successfully', 'OK', {
                        verticalPosition: 'top',
                        duration        : 5000,
                        panelClass: ['snackbar-success']
                    });
                    this.router.navigate(['.', { relativeTo: this.route }]);
                },
                error => {
                    this.loading = false;
                    this._matSnackBar.open('Message template creation error - ' + error, 'OK', {
                        verticalPosition: 'top',
                        duration        : 5000,
                        panelClass: ['snackbar-error']
                    });
                });
    }

    private updateMessageTemplate() {
        this.messageTemplateService.update(this.id, this.form.value)
            .pipe(first())
            .subscribe(
                data => {
                    this._matSnackBar.open('Update successful', 'OK', {
                        verticalPosition: 'top',
                        duration        : 5000,
                        panelClass: ['snackbar-success']
                    });
                    this.router.navigate(['..', { relativeTo: this.route }]);
                },
                error => {
                    this._matSnackBar.open('Message template update error - ' + error, 'OK', {
                        verticalPosition: 'top',
                        duration        : 5000,
                        panelClass: ['snackbar-error']
                    });
                    this.loading = false;
                });
    }

    showTestMessageDialog(event: MessageTemplate) {
        this.deleteDialogRef = this._matDialog.open(TestMessageDialogComponent, {
            panelClass: 'test-message-dialog',
            data      : {
                action : 'show',
                event: event,
            },
            width: '350px',
        });

        this.deleteDialogRef.afterClosed()
            .subscribe((response: FormGroup) => {
                if (!response) {
                    return;
                }

                this.messageTemplateService.test({
                    'name': event.name,
                    'description': event.description,
                    'notificationAccount': response['notificationAccount'],
                    'receiver': response['receiver']
                }).pipe(first())
                    .subscribe(
                        data => {
                            this._matSnackBar.open('Test successful', 'OK', {
                                verticalPosition: 'top',
                                duration        : 5000,
                                panelClass: ['snackbar-success']
                            });
                        },
                        error => {
                            this._matSnackBar.open('Test error - ' + error, 'OK', {
                                verticalPosition: 'top',
                                duration        : 5000,
                                panelClass: ['snackbar-error']
                            });
                            this.loading = false;
                        });

                this.deleteDialogRef.close();

            }, (err) => {
                console.log(err);
            });
    }
}
